import _ from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Button, Comment, Dropdown, Form, Header, Menu, Segment } from 'semantic-ui-react'
import { useFormData } from '../../hooks/formHooks';
import { restRequest } from '../../http';
import { CommentData } from '../../models/comment_model';
import { formatDateTime } from '../../utils';
import { TextEditor } from './TextEditor';
import { notificationControl } from '../../controls/notification-control';
import { useCurrentUser } from '../../hooks/auth-hook';
import { DataFile } from '../../models/common_model';
import { LanguageContext } from '../../context';

export function Comments(props: { containerType: number, containerId: number }) {
    const { containerType, containerId } = props;
    const [comments, setComments] = useState<CommentData[]>([]);
    const [replyToId, setReplyToId] = useState<number>();
    const [editingId, setEditingId] = useState<number>();
    const [loading, setLoading] = useState(false);
    const [freshNumber, setFreshNumber] = useState(0);
    const [t] = useContext(LanguageContext);

    const [addFormData, setAddFormField, setAddFormData] = useFormData({
        container_type: containerType,
        container_id: containerId,
        content: ''
    });

    const onCreate = useCallback(data => {
        setLoading(true);

        if (data.editingId) {
            return restRequest<CommentData>(`comments/${data.editingId}`, data, 'put').then(result => {
                setComments(cs => {
                    const replaces = (cs: CommentData[]) => {
                        for (let i = 0; i < cs.length; i++) {
                            if (cs[i].id === result.id) {
                                cs[i] = { ...result, children: cs[i].children };
                            } else if (cs[i]?.children) {
                                replaces(cs[i].children!)
                            }
                        }
                    }

                    replaces(cs);

                    return cs;

                });
            }).finally(() => setLoading(false));
        }

        return restRequest<CommentData>(`comments`, data, 'post').then(newData => {
            setComments(cs => addToTree(cs, newData))
        }).finally(() => setLoading(false));
    }, [setComments]);

    const onDelete = useCallback(id => {
        setLoading(true);
        return restRequest<CommentData>(`comments/${id}`, null, 'delete').then(() => {
            setFreshNumber(n => n + 1);
        }).finally(() => setLoading(false));
    }, [setComments]);

    const onAddComment = useCallback((e: any) => {
        e.preventDefault();

        if (!addFormData.content) {
            notificationControl.showError('can\'t be empty');
            return;
        }

        onCreate({ ...addFormData, parent_id: 0 }).then(() => {
            setAddFormData({
                container_type: containerType,
                container_id: containerId,
                content: ''
            })
        })
    }, [replyToId, addFormData])


    useEffect(() => {
        setLoading(true);
        restRequest<CommentData[]>(`comments/${containerType}/${containerId}`).then(comments => setComments(makeCommentTree(comments))).finally(() => setLoading(false));
    }, [containerType, containerId, freshNumber]);


    return <Segment loading={loading}>
        <Header as='h3' dividing>
            {t('Comments')}
        </Header>
        <Comment.Group>
            {comments?.map(c => <CommentTree containerType={containerType} containerId={containerId} key={c.id} comment={c} replyToId={replyToId} editingId={editingId} onReplyTo={setReplyToId} onEditing={setEditingId} onCreate={onCreate} onDelete={onDelete} />)}

            <Form reply>
                <TextEditor name="content" value={addFormData.content} onChange={setAddFormField} placeholder={t("Write a comment...")} />
                
                <Button style={{marginTop: '0.5rem'}} onClick={onAddComment} content={t('Send')} primary size='small' />
            </Form>
        </Comment.Group>
    </Segment>
}

function CommentTree(props: { containerType: number, containerId: number, comment: CommentData, replyToId?: number, editingId?: number, onReplyTo: (id: number) => void, onCreate: (data: any) => Promise<any>, onEditing: (id: number) => void, onDelete: (id: number) => void }) {
    const { containerType, containerId, comment, replyToId, editingId, onReplyTo, onCreate, onEditing, onDelete } = props;

    const [formData, setField, setFormData] = useFormData({
        container_type: containerType,
        container_id: containerId,
        content: ''
    });
    const [t] = useContext(LanguageContext);


    const onReply = useCallback((e: any) => {
        e.preventDefault();

        if (!formData.content) {
            notificationControl.showError('can\'t be empty');
            return;
        }

        onCreate({ ...formData, parent_id: replyToId }).then(() => {
            setFormData({
                container_type: containerType,
                container_id: containerId,
                content: ''
            });
            onReplyTo(0)
        })
    }, [replyToId, formData, onCreate]);

    const onEdit = useCallback((e: any) => {
        e.preventDefault();

        if (!formData.content) {
            notificationControl.showError('can\'t be empty');
            return;
        }

        onCreate({ ...formData, editingId }).then(() => {
            setFormData({
                container_type: containerType,
                container_id: containerId,
                content: ''
            });
            onEditing(0)
        })
    }, [replyToId, formData, onCreate]);


    let childrenHtml = null;
    if (comment.children && comment.children.length > 0) {
        childrenHtml = <Comment.Group>
            {comment.children.map(c =>
                <CommentTree key={c.id} containerType={containerType} containerId={containerId} comment={c} replyToId={replyToId} editingId={editingId} onReplyTo={onReplyTo} onCreate={onCreate} onDelete={onDelete} onEditing={onEditing}></CommentTree>
            )}
        </Comment.Group>;
    }

    return <>
        <Comment>
            <Comment.Content>
                <Comment.Author as="span">{comment.author.name}</Comment.Author>
                <Comment.Metadata>
                    <div>{formatDateTime(comment.created_at)}</div>

                    <Dropdown
                        className='mini'
                        floating
                        icon="ellipsis vertical"
                    >
                        <Dropdown.Menu>
                            {comment.editable && <Menu.Item onClick={e => {
                                e.preventDefault();
                                onEditing(comment.id);
                                setField(null, { name: 'content', value: comment.content });
                            }}>
                                {t('Edit')}
                            </Menu.Item>}
                            <Menu.Item onClick={e => {
                                e.preventDefault();
                                onReplyTo(comment.id);
                            }}>
                                {t('Reply')}
                            </Menu.Item>
                            {comment.editable && <Menu.Item onClick={e => {
                                e.preventDefault();
                                onDelete(comment.id);
                            }}>
                                {t('Delete')}
                            </Menu.Item>}
                        </Dropdown.Menu>
                    </Dropdown>
                </Comment.Metadata>
                <Comment.Text>
                    {editingId === comment.id ? <>
                        <TextEditor name="content" value={formData.content} onChange={setField} />
                        <Button onClick={onEdit} content='Edit' primary size="mini" />
                        <Button as="a" onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            onEditing(0);
                        }} content="Cancel" size="mini" />
                    </> : <>
                        <div dangerouslySetInnerHTML={{ __html: comment.content }}></div>
                    </>}
                </Comment.Text>
                <Comment.Actions>

                </Comment.Actions>
                {comment.id === replyToId && <Form reply>
                    <TextEditor name="content" value={formData.content} onChange={setField} />
                    <Button onClick={onReply} content='Reply' primary size="mini" />
                    <Button as="a" onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        onReplyTo(0);
                    }} content="Cancel" size="mini" />
                </Form>}
            </Comment.Content>
            {childrenHtml}
        </Comment>
    </>
}


function makeCommentTree(list: CommentData[]) {
    const root = _.remove(list, c => !c.parent_id)

    const queue: CommentData[] = [...root]
    while (queue.length > 0) {
        const p = queue.shift()
        p!.children = _.remove(list, c => c.parent_id === p!.id)
        for (const c of p!.children) {
            queue.push(c)
        }
    }


    return root
}

function addToTree(list: CommentData[], item: CommentData) {

    if (!item.parent_id) {
        list.unshift(item);
    } else {
        const queue = [...list];
        while (queue.length > 0) {
            const p = queue.shift()
            if (p!.id === item.parent_id) {
                if (p?.children) {
                    p.children.unshift(item)
                } else {
                    p!.children = [item]
                }

                break
            } else {
                if (p?.children) {
                    for (const c of p.children) {
                        queue.push(c)
                    }
                }
            }
        }
    }

    return list;
}